import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  RiLineChartLine,
  RiFileListLine,
  RiMoneyDollarCircleLine,
} from "react-icons/ri";
import { FiPlus } from "react-icons/fi";
import MonthClosure from "./components/MonthClosure";
import {
  Container,
  TabContentWrapper,
  SectionBlock,
  SectionHeader,
  SectionTitleWrapper,
  SectionTitleRow,
  SectionTitle,
  SectionSubtitle,
  BlockHeaderRight,
} from "./styles";
import UserBills from "./components/UserBills";
import { Transactions } from "./components/Transactions";
import { useAuth } from "../../hooks/AuthContext";
import { useOnboard } from "../../hooks/OnboardContext";
import { i18n } from "../../localization/i18n";
import { PageTabs, PageTabItem } from "../../components/PageTabs";
import { PrimaryButton } from "../../components/PrimaryButton";
import api from "../../services/api";

export type LicenseeFinancialTab = "reports" | "bills" | "transactions";

interface FinancialProps {
  initialTab?: number;
}

const TAB_ROUTES: Record<LicenseeFinancialTab, string> = {
  reports: "/financial/reports",
  bills: "/financial/bills",
  transactions: "/financial/transactions",
};

export function Financial({ initialTab }: FinancialProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    licenseeFixcoins,
    isFixiter: authIsFixiter,
    isLicenseeWithPrinter: authIsLicenseeWithPrinter,
  } = useAuth();
  const {
    licensee: onboardLicensee,
    isFixiter: onboardIsFixiter,
    isLicenseeWithPrinter: onboardIsLicenseeWithPrinter,
  } = useOnboard();
  const isLicenseeWithPrinter =
    onboardIsLicenseeWithPrinter || authIsLicenseeWithPrinter;
  const isFixiter = isLicenseeWithPrinter
    ? false
    : onboardIsFixiter || authIsFixiter;

  const targetLicenseeId =
    user?.person?.licensee?.id ||
    (user as any)?.licensee?.id ||
    onboardLicensee?.id;

  const [fixcoinsBalance, setFixcoinsBalance] = useState<number>(
    Number(licenseeFixcoins || 0)
  );
  const [isBuyFixcoinModalOpen, setIsBuyFixcoinModalOpen] = useState(false);

  useEffect(() => {
    setFixcoinsBalance(Number(licenseeFixcoins || 0));
  }, [licenseeFixcoins]);

  const fetchFixcoinsBalance = useCallback(async () => {
    if (!targetLicenseeId) return;
    try {
      const response = await api.get(`/transactions/fixcoin/${targetLicenseeId}`);
      setFixcoinsBalance(Number(response.data || 0));
    } catch (error) {
      console.error("Failed to fetch FixCoins balance:", error);
    }
  }, [targetLicenseeId]);

  useEffect(() => {
    fetchFixcoinsBalance();
  }, [fetchFixcoinsBalance]);

  const activeTab: LicenseeFinancialTab = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes("/financial/bills")) return "bills";
    if (path.includes("/financial/transactions")) return "transactions";
    if (path.includes("/financial/reports")) return "reports";
    if (initialTab === 1) return "bills";
    if (initialTab === 2) return "transactions";
    return "reports";
  }, [location.pathname, initialTab]);

  const tabs: PageTabItem<LicenseeFinancialTab>[] = useMemo(
    () => [
      {
        id: "reports",
        label: `${i18n.t("dashboard.header")}`,
        icon: <RiLineChartLine size={18} />,
      },
      {
        id: "bills",
        label: `${i18n.t("bills.bills")}`,
        icon: <RiFileListLine size={18} />,
      },
      {
        id: "transactions",
        label: "FixCoins",
        icon: <RiMoneyDollarCircleLine size={18} />,
        badge: Number(fixcoinsBalance || 0).toLocaleString("pt-BR"),
        badgeVariant: "default",
      },
    ],
    [fixcoinsBalance]
  );

  if (isFixiter) {
    return <Navigate to="/dashboard" replace />;
  }

  if (initialTab === undefined && location.pathname === "/financial") {
    return <Navigate to="/financial/reports" replace />;
  }

  const handleTabChange = (tabId: LicenseeFinancialTab) => {
    const targetPath = TAB_ROUTES[tabId];
    if (targetPath && location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  };

  return (
    <Container>
      <PageTabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={handleTabChange}
      />
      <TabContentWrapper>
        {activeTab === "reports" && <MonthClosure />}
        {activeTab === "bills" && <UserBills />}
        {activeTab === "transactions" && (
          <SectionBlock>
            <SectionHeader>
              <SectionTitleWrapper>
                <SectionTitleRow>
                  <RiMoneyDollarCircleLine size={20} color="var(--fixit)" />
                  <SectionTitle>FixCoins</SectionTitle>
                </SectionTitleRow>
                <SectionSubtitle>
                  Histórico de transações e movimentações financeiras de FixCoins.
                </SectionSubtitle>
              </SectionTitleWrapper>
              <BlockHeaderRight>
                <PrimaryButton
                  text="Adicionar FixCoins"
                  icon={<FiPlus size={15} />}
                  onClick={() => setIsBuyFixcoinModalOpen(true)}
                />
              </BlockHeaderRight>
            </SectionHeader>

            <Transactions
              isBuyModalOpen={isBuyFixcoinModalOpen}
              onCloseBuyModal={() => setIsBuyFixcoinModalOpen(false)}
              onSuccess={fetchFixcoinsBalance}
            />
          </SectionBlock>
        )}
      </TabContentWrapper>
    </Container>
  );
}
